import React, {useCallback, useEffect, useRef, useState} from "react";
import moment from "moment/moment";
import {useAuth} from "hooks/useAuth";
import CustomDataTable from "shared/tables/CustomDataTable";
import GenericButton from "shared/ui/GenericButton";
import {APIDeleteTag, APIGetTags} from "../api/testTags";
import useDataTable from "../../../../hooks/useDataTable";

export interface IUserTags {
    id: string;
    name: string;
    created_at: string;
}

const TABLE_COLUMNS = [
    {id: "name", label: "Nume", sortable: true, noWrap: true,},
    {id: "date", label: "Creat la", nowrap: true,},
    {id: "actions", label: "Acțiuni", numeric: true, sticky: true,}
];

const UserTags = () => {
    const { update } = useAuth();

    const {
        loading,
        setLoading,
        tableData,
        setTableData,
        totalRows,
        setTotalRows,
        perPage,
        setPerPage,
        page,
        setPage,
        query,
        submittedQuery,
        orderBy,
        order,
        handleChangePage,
        handleRequestSort,
        handleSearch,
        handleSearchChange,
        handleClearSearch,
    } = useDataTable("", "created_at", undefined, "");

    const pageRef = useRef(page);

    useEffect(() => {
        pageRef.current = page;
    }, [page]);

    const mapTags = (tag: IUserTags) => {
        return {
            name: tag.name,
            date: moment(tag.created_at).locale("ro").format("DD MMMM YYYY"),
            actions: <GenericButton type="delete" onClick={() => confirmDelete(tag)} />,
        };
    };

    const getTags = useCallback(async (currentPage = pageRef.current) => {
        try {
            const response = await APIGetTags({
                page: `?page=${currentPage + 1}`,
                sort: orderBy,
                order: order,
                search: submittedQuery,
            });

            if(!response.data.success) {
                update("error", {show: true, code: "A002"});
                return
            }

            const pageData = response.data.data;

            if (pageData.data.length === 0 && currentPage > 0) {
                const previousPage = currentPage - 1;

                setPage(previousPage);
                await getTags(previousPage);
                return;
            }

            setTableData(response.data.data.data.map((tag: IUserTags) => mapTags(tag)));
            setTotalRows(response.data.data.total);
            setPerPage(response.data.data.per_page);

        } catch {
            update("error", {show: true, code: "A001"});
        } finally {
            setLoading(false);
        }
    }, [order, submittedQuery, update, setPage, setTableData, setTotalRows, setPerPage, setLoading]);

    useEffect(() => {
        setLoading(true);
        getTags();
    }, [page, order, orderBy, submittedQuery]);

    const deleteTag = (id: string, callback: () => void) => {
        update("loading", { show: true, modal: true });
        APIDeleteTag({ id })
            .then((response) => {
                if (response.data.success) {
                    // console.log("deleteTeacher OK", response);
                   getTags(pageRef.current);
                } else {
                    update("error", { show: true, code: "A002" });
                }
                callback();
            })
            .catch(() => update("error", { show: true, code: "A001" }))
            .finally(() => update("loading", { show: false }));
    };

    const confirmDelete = (tag: IUserTags) => {
        update("confirm", {
            show: true,
            closable: true,
            title: "Ești sigur că vrei să ștergi acest tag?",
            message: <>Tagul <b>{tag.name}</b> va fi șters definitiv!</>,
            returnFunction: (callback: () => void) => {deleteTag(tag.id, callback)},
            buttonYes: "Șterge",
            buttonNo: "Închide",
        });
    };

    return (
        <CustomDataTable
            loading={loading}
            columns={TABLE_COLUMNS}
            rows={tableData}
            rowsPerPage={perPage}
            page={page}
            totalRows={totalRows}
            onPageChange={handleChangePage}
            handleRequestSort={handleRequestSort}
            orderBy={orderBy}
            order={order}
            hasSearch={true}
            onChange={handleSearchChange}
            onSearch={handleSearch}
            searchValue={query}
            onClearSearch={handleClearSearch}
        />
    );
}

export default UserTags;